import { Injectable } from '@angular/core';
import { ErrorEvent, ErrorReporter, FeatureFlag, Logger } from '@mode/shared/contract-common';
import { StringHelpers } from '@mode/shared/util-js';
import * as angular from 'angular';
import { bind, chain, each, forOwn } from 'lodash';
import { Resource } from '@mode/shared/util-halogen';
import { ModeEntryPointFactoryService } from '../api/mode-entry-point.factory';
import { QAdapterService } from '../migration/q-adapter.service';
import { ModelCache } from '../model-cache/model-cache';
import { extend } from './extend';
import { ModeModelBase, ModeModelInjectables } from './model';
import { ModelNameService } from './model-name.service';

@Injectable({ providedIn: 'root' })
export class ModelAccessorInjectables {
  constructor(
    public $q: QAdapterService,
    public modeEntryPointFactory: ModeEntryPointFactoryService,
    public modelNameService: ModelNameService,
    public logger: Logger,
    public modelCache: ModelCache,
    public errorReporter: ErrorReporter,
    public modelInjectables: ModeModelInjectables
  ) {}
}

export abstract class ModelAccessorBase<T extends ModeModelBase> {
  name = 'model';

  idAttribute = 'id';

  /**
   * @deprecated prefer modelConstructor and createModel instead
   */
  model: new (resource?, opts?) => T;

  cache: ModelCache.Cache = undefined;

  parentModel?: string = undefined;

  parentIdAttribute = 'id';

  createAttributes: string[] = [];

  customCreateFunction = undefined;
  getIdFromLink?: (link: string) => string = undefined;

  constructor(
    protected injectables: ModelAccessorInjectables,
    // temporary to help with migration
    migrated: 'migrated' | 'unmigrated',
    // This shouldn't be optional, but necessary during migration
    cacheId?: string
  ) {
    // To maintain backwards compatibility with new this.model()
    if (migrated === 'migrated') {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const self = this;
      this.model = function (resource?, opts?) {
        return self.createModel(resource, opts);
        // I don't know how to tell the type system that this is a new-able function,
        // but I want to leave the model declaration as a 'new' for better typing elsewhere.
        // Casting to unknown then the new-able function type
      } as unknown as new (resource?, opts?) => T;
    }

    if (cacheId) {
      this.cache = this.injectables.modelCache.getOrCreate(cacheId);
    }
  }

  abstract createModel<T>(resource: Resource, opts?);

  private modelCreationFallback(resource?, opts?) {
    this.injectables.errorReporter.notify({
      error: new Error('No Model Constructor provided. Defaulting to ModeModelBase'),
    });
    const model = new ModeModelBase(this.injectables.modelInjectables);
    model.$initialize(resource, opts);
    return model;
  }

  find(params) {
    if (
      angular.isUndefined(params) ||
      (angular.isUndefined(params[this.idAttribute]) &&
        angular.isUndefined(params[this.parentModel]) &&
        angular.isUndefined(params.permalink))
    ) {
      return this.injectables.$q.reject(
        new Error('Invalid parameters for ' + this.name + '.  Needed: ' + this.idAttribute)
      );
    }

    if (angular.isUndefined(params[this.idAttribute]) && angular.isUndefined(params.permalink)) {
      // Get from parent
      const parentModel = this.injectables.modelNameService.get(this.parentModel);
      const parent = parentModel.createModel(undefined);
      if (angular.isUndefined(parent[this.name])) {
        return this.injectables.$q.reject(
          new Error('Invalid parameters for ' + this.name + '.  Needed: ' + this.parentModel)
        );
      }
    }

    if (
      (!params.includes || (params && angular.isDefined(params.refresh) && !params.refresh)) &&
      this.cache &&
      (<ModelCache.Cache>this.cache).get(params[this.idAttribute])
    ) {
      return (<ModelCache.Cache>this.cache).get(params[this.idAttribute]);
    }

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const deferred = this.injectables.$q.defer();
    const childId = params[this.idAttribute];

    if (params.useEntryPath || params.permalink) {
      //Try mode api entry point first or permalink if given
      const entryPoint = this.injectables.modeEntryPointFactory.create(StringHelpers.dash(this.name), params.permalink);
      entryPoint
        .$enter(params)
        .then((modeResource) => {
          deferred.resolve(self.createModel(modeResource));
        })
        .catch(fallback);
    } else {
      fallback();
    }

    function fallback() {
      self
        ._findParent(params)
        .then((parent) => {
          self._getFromParent(parent, deferred, childId, params);
        })
        .catch((e) => {
          if (self.cache) {
            (<ModelCache.Cache>self.cache).remove(childId);
          }
          deferred.reject(e);
        });
    }

    if (this.cache && params[this.idAttribute]) {
      if ((<ModelCache.Cache>this.cache).get(params[this.idAttribute])) {
        // already cached so update and return local model after update
        const cachedDeferred = this.injectables.$q.defer();
        deferred.promise
          .then((m) => {
            return self.putLocal(m).then((res) => {
              return cachedDeferred.resolve(res);
            });
          })
          .catch(cachedDeferred.reject);
        return cachedDeferred.promise;
      } else {
        (<ModelCache.Cache>this.cache).put(params[this.idAttribute], deferred.promise);
      }
    }
    return deferred.promise;
  }

  _getFromParent(parent, deferred, childId, params) {
    const pluralName = StringHelpers.pluralize(this.name);
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    const saveModel = (modeResource, isNew) => {
      const newModel = self.createModel(modeResource);
      newModel.parent = parent[self.parentIdAttribute];
      if (isNew) {
        newModel['_new_' + self.name] = true;
      }
      deferred.resolve(newModel);
      if (self.cache && !(<ModelCache.Cache>self.cache).get(newModel[self.idAttribute])) {
        (<ModelCache.Cache>self.cache).put(newModel[self.idAttribute], deferred.promise);
      }
    };

    // First check for hasOne relationship with parent
    if (parent && angular.isFunction(parent[this.name])) {
      parent[this.name].call(this, params).then(saveModel).catch(deferred.reject);
    }

    // Then check for hasMany relationship
    else if (parent && angular.isFunction(parent[pluralName])) {
      // calling built function based on model name
      parent[pluralName].call(this, childId, params).then(saveModel).catch(deferred.reject);
    } else {
      deferred.reject(new Error('Model ' + parent._name + ' has no ' + pluralName));
    }
  }

  _findParent(params) {
    if (angular.isDefined(this.parentModel)) {
      const parentParams = angular.copy(params);
      parentParams[this.parentIdAttribute] = parentParams[this.parentModel];
      delete parentParams.includes;
      return this.injectables.modelNameService.get(this.parentModel).find(parentParams);
    } else {
      return this.injectables.$q.reject(new Error('No parent relationship or entry point found'));
    }
  }

  watch(scope, handler) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    return scope.$watchCollection(
      () => {
        return (<ModelCache.Cache>self.cache).all();
      },
      (newCollection) => {
        this.injectables.$q
          .all(newCollection)
          .then((models) => {
            handler.call(self, models);
          })
          .catch(() => {
            throw Error('failed to get collection');
          });
      }
    );
  }

  findAll(params) {
    const deferred = this.injectables.$q.defer();

    if (this.cache) {
      this.injectables.$q
        .all(
          (<ModelCache.Cache>this.cache).where(() => {
            return true;
          })
        )
        .then((models) => {
          if (angular.isDefined(params) && angular.isDefined(params.where)) {
            const results = chain(models)
              .filter((model) => {
                let select = true;
                forOwn(params.where, (prop, key) => {
                  if (angular.isFunction(prop)) {
                    //TODO: saftey check this function return
                    select = prop(model[key]);
                  } else if (model[key] !== prop) {
                    select = false;
                  }
                });
                return select;
              })
              .sortBy((model) => {
                if (angular.isFunction(params.sort)) {
                  return params.sort(model);
                } else {
                  return undefined; // dont sort
                }
              })
              .value();

            deferred.resolve(results);
          } else {
            deferred.resolve(models);
          }
        });
    } else {
      deferred.resolve([]);
    }

    return deferred.promise;
  }

  create(args, model) {
    const unresolvedArgs = [];
    angular.forEach(this.createAttributes, (attribute) => {
      if (angular.isUndefined(args) || angular.isUndefined(args[attribute])) {
        unresolvedArgs.push(attribute);
      }
    });
    if (unresolvedArgs.length) {
      return this.injectables.$q.reject(new Error('Invalid parameters.  Needed: ' + unresolvedArgs.join(',')));
    }

    const deferred = this.injectables.$q.defer();
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    args[this.parentIdAttribute] = args[this.parentModel];
    const parentModel = this.injectables.modelNameService.get(this.parentModel);
    parentModel
      .find(args)
      .then(angular.bind(self, self._getAndCreateNewItem, args.params, deferred, model))
      .then(deferred.resolve)
      .catch((error) => {
        if (angular.isUndefined(error.status) && angular.isFunction(self.customCreateFunction)) {
          //if we didn't make a call try custom create path
          self
            .customCreateFunction(args)
            .then(angular.bind(self, self._saveAll, undefined, model))
            .then(deferred.resolve)
            .catch(deferred.reject);
        } else {
          deferred.reject(error);
        }
      });

    return deferred.promise;
  }

  _saveAll(parent, model, modeResource) {
    if (angular.isFunction(modeResource.hasEmbedded) && modeResource.hasEmbedded('succeeded')) {
      const promiseArray = [];

      each(
        modeResource._embedded.succeeded,
        bind((resource) => {
          promiseArray.push(this._saveModel(parent, model, resource));
        }, this)
      );

      return this.injectables.$q.all(promiseArray).then(() => {
        return modeResource;
      });
    } else {
      return this._saveModel(parent, model, modeResource);
    }
  }

  _saveModel(parent, model, modeResource) {
    const saveDeferred = this.injectables.$q.defer(),
      newModel = this.createModel(modeResource);

    if (angular.isFunction(modeResource.hasEmbedded) && modeResource.hasEmbedded(this.parentModel)) {
      const parentModel = this.injectables.modelNameService.get(this.parentModel);
      const parent = parentModel.createModel(modeResource.embedded(this.parentModel));
      parentModel.putLocal(parent);
    }
    if (parent) {
      newModel.parent = parent[this.parentIdAttribute];
      if (parent._resource.hasLink(StringHelpers.pluralize(StringHelpers.underscore(this.name)))) {
        const collectionId = parent._resource
          .links(StringHelpers.pluralize(StringHelpers.underscore(this.name)))
          .href.split('?')[0];
        newModel.addToCollection(collectionId);
      } else {
        this.injectables.logger.debug('created resource ' + newModel._name + ' has no parent reference');
      }
    }

    if (model && model['_new_' + model._name]) {
      // if we already have a new local model, replace it and its id
      const oldId = model[this.idAttribute];
      model.extendAttributes(newModel);

      delete model['_new_' + model._name];
      if (this.cache && (<ModelCache.Cache>this.cache).get(oldId)) {
        (<ModelCache.Cache>this.cache).remove(oldId);
        model[this.idAttribute] = newModel[this.idAttribute];
        (<ModelCache.Cache>this.cache).put(newModel[this.idAttribute], saveDeferred.promise);
        saveDeferred.resolve(model);
      }
    } else if (newModel.mapping_id && this.cache && (<ModelCache.Cache>this.cache).get(newModel.mapping_id)) {
      // if there is a mapping id stored in cache replace it and its id
      const oldModel = (<ModelCache.Cache>this.cache).get(newModel.mapping_id).then((oldModel) => {
        (<ModelCache.Cache>this.cache).remove(newModel.mapping_id);
        oldModel.extendAttributes(newModel);
        oldModel[this.idAttribute] = newModel[this.idAttribute];

        delete oldModel['_new_' + this.name];
        (<ModelCache.Cache>this.cache).put(newModel[this.idAttribute], saveDeferred.promise);
        saveDeferred.resolve(oldModel);
      });
    } else if (this.cache && (<ModelCache.Cache>this.cache).get(newModel[this.idAttribute])) {
      const oldModel = (<ModelCache.Cache>this.cache).get(newModel[this.idAttribute]).then((oldModel) => {
        oldModel.extendAttributes(newModel);
        delete oldModel['_new_' + this.name];
        saveDeferred.resolve(oldModel);
      });
    } else {
      if (this.cache) {
        (<ModelCache.Cache>this.cache).put(newModel[this.idAttribute], saveDeferred.promise);
      }
      saveDeferred.resolve(newModel);
    }

    return saveDeferred.promise;
  }

  _getAndCreateNewItem(params, deferred, model, parent) {
    const pluralName = StringHelpers.pluralize(this.name);
    if (!angular.isFunction(parent['new_' + pluralName])) {
      return this.injectables.$q.reject(new Error('Create path not found'));
    }

    const newItem = parent['new_' + pluralName].call(this);
    if (angular.isUndefined(newItem) || !angular.isFunction(newItem.hasForm) || !newItem.hasForm('create')) {
      return this.injectables.$q.reject(new Error('Create form not found'));
    }
    return newItem
      .forms('create')
      .$submit(params)
      .then(angular.bind(this, this._saveAll, parent, model))
      .catch(deferred.reject);
  }

  validate(args, timeout) {
    const unresolvedArgs = [];
    angular.forEach(this.createAttributes, (attribute) => {
      if (angular.isUndefined(args) || angular.isUndefined(args[attribute])) {
        unresolvedArgs.push(attribute);
      }
    });
    if (unresolvedArgs.length) {
      return this.injectables.$q.reject(new Error('Invalid parameters.  Needed: ' + unresolvedArgs.join(',')));
    }

    const deferred = this.injectables.$q.defer();
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;

    args[this.parentIdAttribute] = args[this.parentModel];
    const parentModel = this.injectables.modelNameService.get(this.parentModel);
    parentModel
      .find(args)
      .then(angular.bind(self, self._getAndValidateNewItem, args.params, deferred, timeout))
      .then(deferred.resolve)
      .catch(deferred.reject);

    return deferred.promise;
  }

  _getAndValidateNewItem(params, deferred, timeout, parent) {
    const pluralName = StringHelpers.pluralize(this.name);

    if (!angular.isFunction(parent['validate_' + pluralName])) {
      return this.injectables.$q.reject(new Error('Validate path not found'));
    }

    const newItem = parent['validate_' + pluralName].call(this);
    if (angular.isUndefined(newItem) || !angular.isFunction(newItem.hasForm) || !newItem.hasForm('validate')) {
      return this.injectables.$q.reject(new Error('Validate path not found'));
    }
    return newItem.forms('validate').$submit(params, undefined, timeout);
  }

  newModel(params) {
    const deferred = this.injectables.$q.defer(),
      pluralName = StringHelpers.pluralize(this.name),
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      self = this;

    const saveModel = (parent, modeResource) => {
      const newModel = self.createModel(modeResource);
      if (parent) {
        newModel.parent = parent[self.parentIdAttribute];
        if (parent._resource.hasLink(StringHelpers.pluralize(StringHelpers.underscore(self.name)))) {
          const collectionId = parent._resource
            .links(StringHelpers.pluralize(StringHelpers.underscore(self.name)))
            .href.split('?')[0];
          if (!params || !params.noCollection) {
            newModel.addToCollection(collectionId);
          }
        } else {
          this.injectables.logger.warn('created resource ' + newModel._name + ' has no parent reference');
        }
      }
      // newModel
      newModel['_new_' + self.name] = true;

      deferred.resolve(newModel);
      if (self.cache) {
        (<ModelCache.Cache>self.cache).put(newModel[self.idAttribute], deferred.promise);
      }
    };

    const getNewModel = function (parent) {
      if (!angular.isFunction(parent['new_' + pluralName])) {
        return this.injectables.$q.reject(new Error('Create path not found for new model'));
      }

      const newItem = parent['new_' + pluralName].call(this);
      if (angular.isUndefined(newItem) || !angular.isFunction(newItem.hasLink) || !newItem.hasLink('self')) {
        return this.injectables.$q.reject(new Error('Create form not found for new model'));
      }

      return newItem
        .links('self')
        .$follow(params)
        .then(angular.bind(this, saveModel, parent));
    };

    const entryPoint = this.injectables.modeEntryPointFactory.create(this.name);
    entryPoint
      .$enter(params)
      .then(getNewModel)
      .catch(angular.bind(this, this._findParent, params))
      .then(getNewModel)
      .catch((e) => {
        deferred.reject(e);
      });

    return deferred.promise;
  }

  update(model, params) {
    const deferred = this.injectables.$q.defer(),
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      self = this;

    if (model._resource && model._resource.hasForm('edit')) {
      model._resource
        .forms('edit')
        .$submit(params || model.toParams())
        .then((modeResource) => {
          const newModel = self.createModel(modeResource);
          if (self.cache) {
            if ((<ModelCache.Cache>self.cache).get(newModel[self.idAttribute])) {
              (<ModelCache.Cache>self.cache).get(newModel[self.idAttribute]).then((oldModel) => {
                oldModel.extendAttributes(newModel);
                if (oldModel['_new_' + model._name]) {
                  // no longer a new model
                  delete oldModel['_new_' + model._name];
                }
                // need to support new resource based rxstore service that saves
                // new state on the resource until we move off this model layer entirely
                if (oldModel._resource && oldModel._resource['_new_' + model._name]) {
                  delete oldModel._resource['_new_' + model._name];
                }
                deferred.resolve(oldModel);
              });
            } else {
              (<ModelCache.Cache>self.cache).put(newModel[self.idAttribute], deferred.promise);
              deferred.resolve(newModel);
            }
          } else {
            deferred.resolve(newModel);
          }
        })
        .catch(deferred.reject);
    } else {
      return this.injectables.$q.reject(new Error('Model ' + this.name + ' has no update form'));
    }
    return deferred.promise;
  }

  refresh(model, options) {
    const deferred = this.injectables.$q.defer(),
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      self = this;

    model
      .reload(options)
      .then((modeResource) => {
        const newModel = self.createModel(modeResource);
        if (self.cache) {
          if ((<ModelCache.Cache>self.cache).get(newModel[self.idAttribute])) {
            (<ModelCache.Cache>self.cache).get(newModel[self.idAttribute]).then((oldModel) => {
              oldModel.extendAttributes(newModel);
              deferred.resolve(oldModel);
            });
          } else {
            (<ModelCache.Cache>self.cache).put(newModel[self.idAttribute], deferred.promise);
            deferred.resolve(newModel);
          }
        } else {
          deferred.resolve(newModel);
        }
      })
      .catch(deferred.reject);

    return deferred.promise;
  }

  destroy(model) {
    if (angular.isDefined(this.cache)) {
      (<ModelCache.Cache>this.cache).remove(model[model.idAttribute]);
    }
  }

  putLocal(model) {
    const deferred = this.injectables.$q.defer();
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    if (angular.isDefined(this.cache)) {
      if (
        (<ModelCache.Cache>this.cache).get(model[this.idAttribute]) ||
        (model.mapping_id && (<ModelCache.Cache>this.cache).get(model.mapping_id))
      ) {
        const doMapping = !(<ModelCache.Cache>this.cache).get(model[this.idAttribute]);
        const tempId = doMapping ? model.mapping_id : model[this.idAttribute];
        (<ModelCache.Cache>this.cache)
          .get(tempId)
          .then((oldModel) => {
            oldModel.extendAttributes(model);

            delete oldModel['_new_' + model._name];
            if (doMapping) {
              this.injectables.logger.debug('Saving over temp model', oldModel);
              (<ModelCache.Cache>self.cache).remove(tempId);
              oldModel._old_mapping_id = model.mapping_id;

              delete oldModel.mapping_id;
              (<ModelCache.Cache>self.cache).put(oldModel.token, deferred.promise);
            }
            deferred.resolve(oldModel);
          })
          .catch((error) => {
            if (angular.isDefined(error) && error.status === 404) {
              // one case where we want to override old failed promise
              if (doMapping) {
                (<ModelCache.Cache>self.cache).remove(model.mapping_id);
              }
              (<ModelCache.Cache>self.cache).put(model[self.idAttribute], deferred.promise);
              deferred.resolve(model);
            } else {
              deferred.reject(error);
            }
          });
      } else {
        if (angular.isDefined(model[this.idAttribute])) {
          (<ModelCache.Cache>this.cache).put(model[this.idAttribute], deferred.promise);
        } else {
          this.injectables.logger.warn('Could not save model in cache - no id specified');
        }
        deferred.resolve(model);
      }
    } else {
      deferred.resolve(model);
    }
    return deferred.promise;
  }
}
export function ModelAccessorAngularJsMigrationAdapter(injectables: ModelAccessorInjectables) {
  class AngularJsCompatibleModel extends ModeModelBase {
    constructor(properties, opts) {
      super(injectables.modelInjectables);
      // eslint-disable-next-line prefer-spread, prefer-rest-params
      this.$initialize.apply(this, arguments);
    }
  }
  class AngularJSCompatibleModelAccessor extends ModelAccessorBase<AngularJsCompatibleModel> {
    override model = AngularJsCompatibleModel;
    override createModel(resource, opts?) {
      return new this.model(resource, opts);
    }
  }
  const ModeModelAccessor: any = function () {};
  ModeModelAccessor.prototype = new AngularJSCompatibleModelAccessor(injectables, 'unmigrated');
  ModeModelAccessor.extend = extend;
  return ModeModelAccessor;
}

ModelAccessorAngularJsMigrationAdapter.$inject = ['ModelAccessorInjectables'];
